import Swiper from "swiper/bundle"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { find, findAll } from "utils/dom"

gsap.registerPlugin(ScrollTrigger)

const mqMobile = window.matchMedia("(max-width: 767px)")
const isMobile = () => mqMobile.matches

// mobile: um pouco mais cedo (viewport menor / seções empilhadas)
const scrollIn = () => (isMobile() ? "top 70%" : "top 55%")

function revealOnScroll(trigger, onEnter) {
	if (!trigger) return
	ScrollTrigger.create({
		trigger,
		start: scrollIn(),
		once: true,
		onEnter,
	})
}

// dispara assim que o bloco entra na tela (sem esperar o centro)
function revealWhenInView(trigger, onEnter) {
	if (!trigger) return
	ScrollTrigger.create({
		trigger,
		start: "top 80%",
		once: true,
		onEnter,
	})
}

function initHero(root) {
	const hero = find(".hm-hero", root)
	if (!hero) return

	const mobile = isMobile()
	const bgs = findAll(".hm-hero__bg", hero)
	const logo = find(".hm-hero__logo", hero)
	const copyBits = findAll(".hm-hero__copy > *", hero)
	// mobile: logo some no CSS; anima a data visível (mobile) e o copy
	const date = mobile
		? find(".hm-hero__date--mobile", hero)
		: find(".hm-hero__date--desktop", hero)
	const contentBits = mobile
		? [date, ...copyBits].filter(Boolean)
		: [logo, ...copyBits, date].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set([...bgs, logo, ...copyBits, ...findAll(".hm-hero__date", hero)].filter(Boolean), {
			opacity: 1,
			clearProps: "transform",
		})
		return
	}

	gsap.set(contentBits, { opacity: 0, y: mobile ? 20 : 28 })

	const tl = gsap.timeline({ defaults: { ease: "power2.out" } })

	tl.fromTo(
		bgs,
		{ opacity: 0 },
		{ opacity: 1, duration: mobile ? 1 : 1.5 },
		0,
	)

	if (mobile) {
		// data primeiro (topo), depois texto + CTA
		if (date) tl.to(date, { opacity: 1, y: 0, duration: 0.65 }, 0.3)
		if (copyBits.length) {
			tl.to(copyBits, { opacity: 1, y: 0, duration: 0.6, stagger: 0.12 }, 0.5)
		}
	} else {
		if (logo) tl.to(logo, { opacity: 1, y: 0, duration: 1 }, 0.6)
		if (copyBits.length) {
			tl.to(copyBits, { opacity: 1, y: 0, duration: 1, stagger: 0.2 }, 1)
		}
		if (date) tl.to(date, { opacity: 1, y: 0, duration: 1 }, 1.3)
	}
}

function pad(n) {
	return String(n).padStart(2, "0")
}

function getCountdownParts(target) {
	const remaining = Math.max(0, target.getTime() - Date.now())
	let diff = remaining
	const days = Math.floor(diff / 86400000)
	diff -= days * 86400000
	const hours = Math.floor(diff / 3600000)
	diff -= hours * 3600000
	const mins = Math.floor(diff / 60000)
	diff -= mins * 60000
	const secs = Math.floor(diff / 1000)
	return { remaining, days, hours, mins, secs }
}

function initCountdown(root) {
	const el = find("[js-home-countdown]", root)
	if (!el) return

	const target = new Date(el.getAttribute("data-target") || "2026-10-29T16:00:00-03:00")
	const daysEl = find("[js-cd-days]", el)
	const hoursEl = find("[js-cd-hours]", el)
	const minsEl = find("[js-cd-mins]", el)
	const secsEl = find("[js-cd-secs]", el)
	const labels = findAll(".hm-countdown__label", el)
	const numEls = [daysEl, hoursEl, minsEl, secsEl].filter(Boolean)

	const write = ({ days, hours, mins, secs }) => {
		if (daysEl) daysEl.textContent = pad(days)
		if (hoursEl) hoursEl.textContent = pad(hours)
		if (minsEl) minsEl.textContent = pad(mins)
		if (secsEl) secsEl.textContent = pad(secs)
	}

	let timer = null
	const startLive = () => {
		if (timer) return
		const tick = () => {
			const parts = getCountdownParts(target)
			write(parts)
			if (parts.remaining === 0) clearInterval(timer)
		}
		tick()
		timer = setInterval(tick, 1000)
	}

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(labels, { opacity: 1 })
		startLive()
		return
	}

	// estado inicial: zeros + labels ocultos
	write({ days: 0, hours: 0, mins: 0, secs: 0 })
	gsap.set(labels, { opacity: 0, y: 10 })
	gsap.set(numEls, { opacity: 0, scale: 0.85 })

	// dispara quando os números entram na tela (não o topo da section —
	// com padding o track ainda estaria off-screen e a animação passava invisível)
	const triggerEl = find(".hm-countdown__track", el) || el
	ScrollTrigger.create({
		trigger: triggerEl,
		start: "top 80%",
		once: true,
		onEnter: () => {
			const parts = getCountdownParts(target)
			const slots = [
				{ el: daysEl, value: parts.days },
				{ el: hoursEl, value: parts.hours },
				{ el: minsEl, value: parts.mins },
				{ el: secsEl, value: parts.secs },
			].filter((s) => s.el)

			const mobile = isMobile()
			const tl = gsap.timeline({
				onComplete: startLive,
			})

			slots.forEach(({ el: numEl, value }, i) => {
				const proxy = { val: 0 }
				const at = i * (mobile ? 0.08 : 0.14)
				tl.to(
					numEl,
					{ opacity: 1, scale: 1, duration: mobile ? 0.5 : 0.7, ease: "power2.out" },
					at,
				)
				tl.to(
					proxy,
					{
						val: value,
						duration: mobile ? 0.8 : 1.2,
						ease: "power2.out",
						onUpdate: () => {
							numEl.textContent = pad(Math.round(proxy.val))
						},
					},
					at,
				)
			})

			tl.to(
				labels,
				{
					opacity: 1,
					y: 0,
					duration: mobile ? 0.45 : 0.7,
					ease: "power2.out",
					stagger: mobile ? 0.06 : 0.11,
				},
				mobile ? 0.26 : 0.45,
			)
		},
	})
}

// A paginação do Figma conta páginas do carrossel, não slides individuais —
// com grid 4x2 um "snap" equivale a 8 cards.
function syncFraction(fractionEl, swiper) {
	if (!fractionEl) return
	const total = swiper.snapGrid?.length || swiper.slides.length
	const current = Math.min(swiper.snapIndex + 1, total)
	fractionEl.innerHTML = `<strong>${pad(current)}</strong>/${pad(total)}`
}

// mantém o fraction em dia em toda mudança que altere o número de páginas
function bindFraction(swiper, fractionEl) {
	if (!fractionEl) return
	const sync = () => syncFraction(fractionEl, swiper)
	sync()
	swiper.on("slideChange", sync)
	swiper.on("breakpoint", sync)
	swiper.on("resize", sync)
}

// Mobile: esconde bolinhas extras — no máx. 4 visíveis. Navegação intacta.
function bindMaxDotsMobile(swiper, max = 4) {
	if (!swiper?.pagination) return
	const mq = window.matchMedia("(max-width: 767px)")

	const apply = () => {
		const bullets = swiper.pagination?.bullets
		if (!bullets?.length) return

		if (!mq.matches || bullets.length <= max) {
			bullets.forEach((b) => {
				b.style.display = ""
			})
			return
		}

		const total = bullets.length
		const current = Math.min(swiper.snapIndex ?? 0, total - 1)
		let start = current - Math.floor((max - 1) / 2)
		start = Math.max(0, Math.min(start, total - max))

		bullets.forEach((b, i) => {
			b.style.display = i >= start && i < start + max ? "" : "none"
		})
	}

	swiper.on("slideChange", apply)
	swiper.on("breakpoint", apply)
	swiper.on("resize", apply)
	mq.addEventListener("change", apply)
	apply()
}

function initHeadliner(root) {
	const el = find("[js-headliner-swiper]", root)
	if (!el) return

	// spaceBetween no mesmo padrão de atrações/galeria (20 → 24 → 32)
	const swiper = new Swiper(el, {
		loop: true,
		slidesPerView: 1,
		spaceBetween: 20,
		autoplay: { delay: 5000, disableOnInteraction: false },
		breakpoints: {
			768: { spaceBetween: 24 },
			1200: { spaceBetween: 32 },
		},
		pagination: {
			el: find("[js-headliner-dots]", root),
			clickable: true,
		},
	})
	bindMaxDotsMobile(swiper)
}

// Grade 4 colunas x 2 linhas, avançando de página em página (Figma 4039:2040)
function initAttractions(root) {
	const el = find("[js-attractions-swiper]", root)
	if (!el) return

	const swiper = new Swiper(el, {
		slidesPerView: 1,
		slidesPerGroup: 1,
		spaceBetween: 20,
		grid: { rows: 1, fill: "row" },
		// no módulo Grid, slidesPerGroup conta COLUNAS (não slides):
		// deve ser igual a slidesPerView para avançar uma página inteira
		breakpoints: {
			768: {
				slidesPerView: 2,
				slidesPerGroup: 2,
				spaceBetween: 24,
				grid: { rows: 2, fill: "row" },
			},
			1200: {
				slidesPerView: 4,
				slidesPerGroup: 4,
				spaceBetween: 32,
				grid: { rows: 2, fill: "row" },
			},
		},
		navigation: {
			prevEl: find("[js-attractions-prev]", root),
			nextEl: find("[js-attractions-next]", root),
		},
		pagination: {
			el: find("[js-attractions-dots]", root),
			clickable: true,
		},
	})

	bindFraction(swiper, find("[js-attractions-fraction]", root))
	bindMaxDotsMobile(swiper)
}

// Mobile é carrossel (Figma 4049:6222); desktop: 3 cards estáticos — Swiper
// só existe <=900px para não poluir setas/dots no desktop
function initTickets(root) {
	const el = find("[js-tickets-swiper]", root)
	if (!el) return

	const mq = window.matchMedia("(max-width: 900px)")
	let swiper = null

	const mount = () => {
		if (swiper) return
		swiper = new Swiper(el, {
			slidesPerView: "auto",
			spaceBetween: 20,
			grabCursor: true,
			simulateTouch: true,
			allowTouchMove: true,
			navigation: {
				prevEl: find("[js-tickets-prev]", root),
				nextEl: find("[js-tickets-next]", root),
			},
			pagination: {
				el: find("[js-tickets-dots]", root),
				clickable: true,
			},
		})
		bindFraction(swiper, find("[js-tickets-fraction]", root))
		bindMaxDotsMobile(swiper)
	}

	const unmount = () => {
		if (!swiper) return
		swiper.destroy(true, true)
		swiper = null
	}

	const sync = () => {
		if (mq.matches) mount()
		else unmount()
	}

	sync()
	mq.addEventListener("change", sync)
}

function initGallery(root) {
	const el = find("[js-gallery-swiper]", root)
	if (!el) return

	const swiper = new Swiper(el, {
		// mobile: mesmo padrão dos highlights (auto + peek ~64px, imagem vertical)
		slidesPerView: "auto",
		spaceBetween: 20,
		breakpoints: {
			768: { slidesPerView: 2.2, spaceBetween: 24 },
			1200: { slidesPerView: 4, spaceBetween: 32 },
		},
		navigation: {
			prevEl: find("[js-gallery-prev]", root),
			nextEl: find("[js-gallery-next]", root),
		},
		pagination: {
			el: find("[js-gallery-dots]", root),
			clickable: true,
		},
	})

	bindFraction(swiper, find("[js-gallery-fraction]", root))
	bindMaxDotsMobile(swiper)
}

// Mobile: carrossel (Figma 4049:9134); >=768: grade 2x2 estática — Swiper
// só existe <=767px (mesmo padrão dos tickets) para o gap/grid do CSS
// desktop não interferir no touch
function initHighlights(root) {
	const el = find("[js-highlights-swiper]", root)
	if (!el) return

	const mq = window.matchMedia("(max-width: 767px)")
	let swiper = null

	const mount = () => {
		if (swiper) return
		swiper = new Swiper(el, {
			slidesPerView: "auto",
			spaceBetween: 20,
			grabCursor: true,
			simulateTouch: true,
			allowTouchMove: true,
			navigation: {
				prevEl: find("[js-highlights-prev]", root),
				nextEl: find("[js-highlights-next]", root),
			},
			pagination: {
				el: find("[js-highlights-dots]", root),
				clickable: true,
			},
		})
		bindFraction(swiper, find("[js-highlights-fraction]", root))
		bindMaxDotsMobile(swiper)
	}

	const unmount = () => {
		if (!swiper) return
		swiper.destroy(true, true)
		swiper = null
	}

	const sync = () => {
		if (mq.matches) mount()
		else unmount()
	}

	sync()
	mq.addEventListener("change", sync)
}

// faixa contínua de logos: cards fixos de 248px (Figma 4039:4432 / mobile 4049:9576)
function initPartners(root) {
	const el = find("[js-partners-swiper]", root)
	if (!el) return

	new Swiper(el, {
		slidesPerView: "auto",
		spaceBetween: 24,
		loop: true,
		autoplay: { delay: 2500, disableOnInteraction: false },
		breakpoints: {
			768: { spaceBetween: 32 },
		},
	})
}

function initFaq(root) {
	const faq = find("[js-home-faq]", root)
	if (!faq) return

	const items = findAll("details", faq) || []
	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

	const closePanel = (item, content, { instant = false } = {}) => {
		if (!content) {
			item.open = false
			return
		}
		if (reduceMotion || instant) {
			gsap.set(content, { height: 0, opacity: 0, marginTop: 0, paddingTop: 0 })
			item.open = false
			return
		}
		gsap.killTweensOf(content)
		gsap.to(content, {
			height: 0,
			opacity: 0,
			marginTop: 0,
			paddingTop: 0,
			duration: 0.32,
			ease: "power2.inOut",
			onComplete: () => {
				item.open = false
			},
		})
	}

	const openPanel = (item, content) => {
		item.open = true
		if (!content) return
		if (reduceMotion) {
			gsap.set(content, { height: "auto", opacity: 1, marginTop: 12, paddingTop: 12 })
			return
		}
		gsap.killTweensOf(content)
		gsap.fromTo(
			content,
			{ height: 0, opacity: 0, marginTop: 0, paddingTop: 0 },
			{
				height: "auto",
				opacity: 1,
				marginTop: 12,
				paddingTop: 12,
				duration: 0.4,
				ease: "power2.out",
			},
		)
	}

	items.forEach((item) => {
		const summary = item.querySelector("summary")
		const content = item.querySelector(":scope > p")
		if (!summary || !content) return

		gsap.set(content, { overflow: "hidden" })
		if (item.open) {
			gsap.set(content, { height: "auto", opacity: 1 })
		} else {
			gsap.set(content, { height: 0, opacity: 0, marginTop: 0, paddingTop: 0 })
		}

		summary.addEventListener("click", (e) => {
			e.preventDefault()
			if (item.open) {
				closePanel(item, content)
				return
			}
			items.forEach((other) => {
				if (other !== item && other.open) {
					closePanel(other, other.querySelector(":scope > p"))
				}
			})
			openPanel(item, content)
		})
	})
}

function parseStatNum(raw) {
	const text = (raw || "").trim()
	const milMatch = text.match(/^([\d.]+)\s*(mil)$/i)
	if (milMatch) {
		return {
			value: Number(milMatch[1].replace(/\./g, "")),
			format: (n) => `${Math.round(n)} mil`,
		}
	}
	const hasDotThousands = /^\d{1,3}(\.\d{3})+$/.test(text)
	const value = Number(text.replace(/\./g, "").replace(",", "."))
	if (!Number.isFinite(value)) {
		return { value: 0, format: () => text }
	}
	return {
		value,
		format: (n) => {
			const rounded = Math.round(n)
			return hasDotThousands ? rounded.toLocaleString("pt-BR") : String(rounded)
		},
	}
}

function initAbout(root) {
	const section = find(".hm-about", root)
	if (!section) return

	const mobile = isMobile()
	const left = find(".hm-about__left", section)
	const rightText = find(".hm-about__right .hm-about__text", section)
	const statsWrap = find(".hm-about__stats", section)
	const title = find(".hm-about__left .hm-display", section)
	const leftParas = findAll(".hm-about__left .hm-about__text p", section)
	const rightParas = findAll(".hm-about__right .hm-about__text p", section)
	const stats = findAll(".hm-stat", section)
	const leftBits = [title, ...leftParas].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set([...leftBits, ...rightParas, ...stats], { opacity: 1, clearProps: "transform" })
		return
	}

	gsap.set(leftBits, { opacity: 0, y: mobile ? 18 : 24 })
	gsap.set(rightParas, { opacity: 0, y: mobile ? 18 : 24 })
	gsap.set(stats, { opacity: 0, y: mobile ? 14 : 20 })

	const numTargets = stats.map((stat) => {
		const numEl = find(".hm-stat__num", stat)
		if (!numEl) return null
		const parsed = parseStatNum(numEl.textContent)
		numEl.textContent = parsed.format(0)
		return { numEl, ...parsed }
	}).filter(Boolean)

	revealWhenInView(left, () => {
		gsap.to(leftBits, {
			opacity: 1,
			y: 0,
			duration: mobile ? 0.65 : 1,
			stagger: mobile ? 0.1 : 0.17,
			ease: "power2.out",
		})
	})

	revealWhenInView(rightText, () => {
		gsap.to(rightParas, {
			opacity: 1,
			y: 0,
			duration: mobile ? 0.6 : 0.9,
			stagger: mobile ? 0.08 : 0.15,
			ease: "power2.out",
		})
	})

	revealWhenInView(statsWrap, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		tl.to(stats, {
			opacity: 1,
			y: 0,
			duration: mobile ? 0.5 : 0.8,
			stagger: mobile ? 0.07 : 0.12,
		}, 0)
		numTargets.forEach(({ numEl, value, format }, i) => {
			const proxy = { val: 0 }
			tl.to(
				proxy,
				{
					val: value,
					duration: mobile ? 0.8 : 1.2,
					ease: "power2.out",
					onUpdate: () => {
						numEl.textContent = format(proxy.val)
					},
				},
				0.07 + i * (mobile ? 0.08 : 0.13),
			)
		})
	})
}

function initDiff(root) {
	const section = find(".hm-diff", root)
	if (!section) return

	const mobile = isMobile()
	const cards = findAll(".hm-diff-card", section)
	const cta = find(".hm-diff__cta", section)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set([...cards, cta].filter(Boolean), { opacity: 1, clearProps: "transform" })
		return
	}

	const duration = mobile ? 0.35 : 0.4
	const step = mobile ? 0.09 : 0.11

	if (cards.length) gsap.set(cards, { opacity: 0, y: mobile ? 16 : 20 })
	if (cta) gsap.set(cta, { opacity: 0, y: mobile ? 12 : 16, scale: 0.96 })

	ScrollTrigger.create({
		trigger: section,
		start: scrollIn(),
		once: true,
		onEnter: () => {
			const tl = gsap.timeline({ defaults: { ease: "power2.out" } })

			cards.forEach((card, i) => {
				tl.to(card, { opacity: 1, y: 0, duration }, i * step)
			})

			if (cta) {
				tl.to(
					cta,
					{
						opacity: 1,
						y: 0,
						scale: 1,
						duration: mobile ? 0.5 : 0.7,
						ease: "power2.out",
					},
					mobile ? 0.16 : 0.23,
				)
			}
		},
	})
}

function initTicketsAnim(root) {
	const section = find(".hm-tickets", root)
	if (!section) return

	const mobile = isMobile() || window.matchMedia("(max-width: 900px)").matches
	const header = find(".hm-tickets__header", section)
	const carousel = find(".hm-tickets__carousel-wrap", section)
	const title = find(".hm-tickets__header .hm-display", section)
	const subtitle = find(".hm-tickets__header p", section)
	const cards = findAll(".hm-ticket-card", section)
	const tags = findAll(".hm-ticket-card__tag", section)
	const cta = find(".hm-tickets__inner > .btn-home-primary", section)
	const nav = find(".hm-tickets__nav", section)
	const headerBits = [title, subtitle].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set([...headerBits, ...cards, cta, nav].filter(Boolean), {
			opacity: 1,
			clearProps: "transform",
		})
		return
	}

	gsap.set(headerBits, { opacity: 0, y: mobile ? 18 : 28 })

	// no carrossel mobile: anima só o 1º card; o resto já visível p/ o swiper
	const animCards = mobile ? cards.slice(0, 1) : cards
	const skipCards = mobile ? cards.slice(1) : []
	if (skipCards.length) gsap.set(skipCards, { opacity: 1, clearProps: "transform" })
	gsap.set(animCards, { opacity: 0, y: mobile ? 36 : 72, rotate: 0 })
	if (!mobile) {
		animCards.forEach((card, i) => {
			gsap.set(card, { rotate: i % 2 === 0 ? -5 : 5 })
		})
	}
	const animTags = mobile ? tags.slice(0, 1) : tags
	gsap.set(animTags, { scale: 0.7, opacity: 0, transformOrigin: "0% 50%" })
	if (cta) gsap.set(cta, { opacity: 0, y: 16, scale: 0.96 })
	if (nav) gsap.set(nav, { opacity: 0 })

	revealWhenInView(header, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (title) tl.to(title, { opacity: 1, y: 0, duration: mobile ? 0.6 : 0.8 }, 0)
		if (subtitle) tl.to(subtitle, { opacity: 1, y: 0, duration: mobile ? 0.55 : 0.75 }, 0.16)
	})

	revealWhenInView(carousel, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		animCards.forEach((card, i) => {
			const at = i * (mobile ? 0.08 : 0.13)
			tl.to(
				card,
				{
					opacity: 1,
					y: 0,
					rotate: 0,
					duration: mobile ? 0.4 : 0.5,
					ease: "power3.out",
				},
				at,
			)
			const tag = animTags[i]
			if (tag) {
				tl.to(
					tag,
					{ opacity: 1, scale: 1, duration: 0.3, ease: "back.out(1.6)" },
					at + 0.1,
				)
			}
		})
		if (nav && mobile) tl.to(nav, { opacity: 1, duration: 0.35 }, 0.2)
	})

	revealWhenInView(cta, () => {
		gsap.to(cta, {
			opacity: 1,
			y: 0,
			scale: 1,
			duration: mobile ? 0.5 : 0.65,
			ease: "power2.out",
		})
	})
}

function initAttractionsAnim(root) {
	const section = find(".hm-attractions", root)
	if (!section) return

	const mobile = isMobile()
	const header = find(".hm-section-header", section)
	const title = find(".hm-section-header .hm-display", section)
	const intro = find(".hm-section-header p", section)
	const banner = find(".hm-attractions__banner", section)
	const list = find(".hm-attractions__list", section)
	const subtitle = find(".hm-attractions__subtitle", section)
	const cards = findAll(".hm-attraction-card", section)
	const nav = find(".hm-attractions__list .hm-carousel-nav", section)
	const ctaBanner = find(".hm-banner-frame--cta", section)

	const bits = [title, intro, banner, subtitle, ...cards, nav, ctaBanner].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(bits, { opacity: 1, clearProps: "transform" })
		return
	}

	gsap.set([title, intro].filter(Boolean), { opacity: 0, y: mobile ? 16 : 24 })
	if (banner) gsap.set(banner, { opacity: 0, y: mobile ? 24 : 36, scale: 0.97 })
	if (subtitle) gsap.set(subtitle, { opacity: 0, y: 16 })

	// mobile: sem flip 3D (corta no swiper); anima 1º card, resto já ok
	const animCards = mobile ? cards.slice(0, 1) : cards
	const skipCards = mobile ? cards.slice(1) : []
	if (skipCards.length) gsap.set(skipCards, { opacity: 1, clearProps: "transform" })
	if (mobile) {
		gsap.set(animCards, { opacity: 0, y: 28 })
	} else {
		gsap.set(animCards, {
			opacity: 0,
			rotateY: (i) => (i % 2 === 0 ? -75 : 75),
			transformPerspective: 900,
			transformOrigin: "50% 50%",
		})
	}
	if (nav) gsap.set(nav, { opacity: 0 })
	if (ctaBanner) gsap.set(ctaBanner, { opacity: 0, y: mobile ? 20 : 28 })

	revealWhenInView(header, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (title) tl.to(title, { opacity: 1, y: 0, duration: mobile ? 0.55 : 0.7 }, 0)
		if (intro) tl.to(intro, { opacity: 1, y: 0, duration: mobile ? 0.5 : 0.65 }, 0.16)
	})

	revealWhenInView(banner, () => {
		gsap.to(banner, {
			opacity: 1,
			y: 0,
			scale: 1,
			duration: mobile ? 0.5 : 0.65,
			ease: "power3.out",
		})
	})

	revealWhenInView(list, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (subtitle) tl.to(subtitle, { opacity: 1, y: 0, duration: 0.45 }, 0)
		animCards.forEach((card, i) => {
			tl.to(
				card,
				mobile
					? { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" }
					: { opacity: 1, rotateY: 0, duration: 0.5, ease: "power3.out" },
				0.1 + i * (mobile ? 0.05 : 0.07),
			)
		})
		if (nav) tl.to(nav, { opacity: 1, duration: 0.4 }, 0.23)
	})

	revealWhenInView(ctaBanner, () => {
		gsap.to(ctaBanner, {
			opacity: 1,
			y: 0,
			duration: mobile ? 0.5 : 0.65,
			ease: "power2.out",
		})
	})
}

function initHighlightsAnim(root) {
	const section = find(".hm-highlights", root)
	if (!section) return

	const mobile = isMobile()
	const title = find(".hm-container > .hm-display", section)
	const grid = find(".hm-highlights__grid", section)
	const cards = findAll(".hm-hl-card", section)
	const nav = find(".hm-highlights__nav", section)
	const video = find(".hm-banner-frame--video", section)
	const gallery = find(".hm-gallery", section)
	const galleryItems = findAll(".hm-gallery__item", section)
	const galleryNav = find(".hm-gallery .hm-carousel-nav", section)

	const bits = [title, ...cards, nav, video, ...galleryItems, galleryNav].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(bits, { opacity: 1, clearProps: "transform" })
		return
	}

	if (title) gsap.set(title, { opacity: 0, y: mobile ? 18 : 28 })

	const animCards = mobile ? cards.slice(0, 1) : cards
	const skipCards = mobile ? cards.slice(1) : []
	if (skipCards.length) gsap.set(skipCards, { opacity: 1, clearProps: "transform" })
	gsap.set(animCards, { opacity: 0, y: mobile ? 28 : 40, scale: mobile ? 0.97 : 0.94 })
	if (nav) gsap.set(nav, { opacity: 0 })
	if (video) gsap.set(video, { opacity: 0, y: mobile ? 22 : 32, scale: 0.97 })

	const animGallery = mobile ? galleryItems.slice(0, 1) : galleryItems
	const skipGallery = mobile ? galleryItems.slice(1) : []
	if (skipGallery.length) gsap.set(skipGallery, { opacity: 1, clearProps: "transform" })
	gsap.set(animGallery, { opacity: 0, y: mobile ? 24 : 36 })
	if (galleryNav) gsap.set(galleryNav, { opacity: 0 })

	revealWhenInView(title, () => {
		gsap.to(title, { opacity: 1, y: 0, duration: mobile ? 0.6 : 0.75, ease: "power2.out" })
	})

	revealWhenInView(grid, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		animCards.forEach((card, i) => {
			tl.to(
				card,
				{ opacity: 1, y: 0, scale: 1, duration: mobile ? 0.35 : 0.45, ease: "power3.out" },
				i * (mobile ? 0.07 : 0.09),
			)
		})
		if (nav) tl.to(nav, { opacity: 1, duration: 0.4 }, 0.16)
	})

	revealWhenInView(video, () => {
		gsap.to(video, {
			opacity: 1,
			y: 0,
			scale: 1,
			duration: mobile ? 0.5 : 0.65,
			ease: "power3.out",
		})
	})

	revealWhenInView(gallery, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		animGallery.forEach((item, i) => {
			tl.to(item, { opacity: 1, y: 0, duration: mobile ? 0.35 : 0.45 }, i * 0.07)
		})
		if (galleryNav) tl.to(galleryNav, { opacity: 1, duration: 0.4 }, 0.16)
	})
}

function initExhibitorAnim(root) {
	const section = find(".hm-exhibitor", root)
	if (!section) return

	const mobile = isMobile()
	const header = find(".hm-section-header", section)
	const title = find(".hm-section-header .hm-display", section)
	const intro = find(".hm-section-header p", section)
	const cardsRow = find(".hm-exhibitor__cards", section)
	const topCards = findAll(".hm-exhibitor__cards .hm-ex-card", section)
	const bottom = find(".hm-exhibitor__bottom", section)
	const wideCard = find(".hm-ex-card--wide", section)
	const ctaCard = find(".hm-ex-card--cta", section)
	const bullets = findAll(".hm-ex-bullets li", section)
	const partners = find(".hm-partners", section)
	const icons = findAll(".hm-exhibitor__cards .hm-ex-card__icon", section)
	const wideIcon = find(".hm-ex-card--wide .hm-ex-card__icon", section)
	const ctaBits = findAll(".hm-ex-card--cta .hm-ex-card__cta-body > *", section)

	const bits = [
		title,
		intro,
		...topCards,
		wideCard,
		ctaCard,
		...bullets,
		partners,
		wideIcon,
		...ctaBits,
	].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(bits, { opacity: 1, clearProps: "transform" })
		gsap.set(icons, { opacity: 1, clearProps: "transform" })
		return
	}

	gsap.set([title, intro].filter(Boolean), { opacity: 0, y: mobile ? 20 : 28 })
	gsap.set(topCards, {
		opacity: 0,
		y: mobile ? 40 : 56,
		scale: mobile ? 0.96 : 0.92,
		rotate: 0,
	})
	if (!mobile) {
		topCards.forEach((card, i) => {
			gsap.set(card, { rotate: i === 1 ? 5 : i === 0 ? -4 : 3 })
		})
	}
	gsap.set(icons, { scale: 0.35, opacity: 0, rotate: -12, transformOrigin: "50% 50%" })
	if (wideCard) {
		gsap.set(wideCard, {
			opacity: 0,
			y: mobile ? 36 : 48,
			scale: mobile ? 0.97 : 0.94,
			x: mobile ? 0 : -24,
		})
	}
	if (wideIcon) gsap.set(wideIcon, { scale: 0.4, opacity: 0, transformOrigin: "50% 50%" })
	if (ctaCard) {
		gsap.set(ctaCard, {
			opacity: 0,
			y: mobile ? 36 : 48,
			scale: mobile ? 0.97 : 0.94,
			x: mobile ? 0 : 24,
		})
	}
	if (ctaBits.length) gsap.set(ctaBits, { opacity: 0, y: 16 })
	gsap.set(bullets, { opacity: 0, y: mobile ? 14 : 18, x: mobile ? 0 : -10 })
	if (partners) gsap.set(partners, { opacity: 0, y: 28 })

	revealWhenInView(header, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (title) tl.to(title, { opacity: 1, y: 0, duration: mobile ? 0.65 : 0.85 }, 0)
		if (intro) tl.to(intro, { opacity: 1, y: 0, duration: mobile ? 0.55 : 0.7 }, 0.14)
	})

	revealWhenInView(cardsRow, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		topCards.forEach((card, i) => {
			const at = i * (mobile ? 0.1 : 0.14)
			tl.to(
				card,
				{
					opacity: 1,
					y: 0,
					scale: 1,
					rotate: 0,
					duration: mobile ? 0.5 : 0.65,
					ease: "power3.out",
				},
				at,
			)
			const icon = icons[i]
			if (icon) {
				tl.to(
					icon,
					{
						opacity: 1,
						scale: 1,
						rotate: 0,
						duration: 0.45,
						ease: "back.out(1.8)",
					},
					at + 0.18,
				)
			}
		})
	})

	revealWhenInView(bottom, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (wideCard) {
			tl.to(
				wideCard,
				{
					opacity: 1,
					y: 0,
					x: 0,
					scale: 1,
					duration: mobile ? 0.55 : 0.7,
					ease: "power3.out",
				},
				0,
			)
		}
		if (wideIcon) {
			tl.to(
				wideIcon,
				{ opacity: 1, scale: 1, duration: 0.4, ease: "back.out(1.7)" },
				0.2,
			)
		}
		if (ctaCard) {
			tl.to(
				ctaCard,
				{
					opacity: 1,
					y: 0,
					x: 0,
					scale: 1,
					duration: mobile ? 0.55 : 0.7,
					ease: "power3.out",
				},
				mobile ? 0.1 : 0.12,
			)
		}
		bullets.forEach((li, i) => {
			tl.to(
				li,
				{
					opacity: 1,
					y: 0,
					x: 0,
					duration: mobile ? 0.35 : 0.4,
					ease: "power2.out",
				},
				0.28 + i * (mobile ? 0.06 : 0.08),
			)
		})
		if (ctaBits.length) {
			tl.to(
				ctaBits,
				{
					opacity: 1,
					y: 0,
					duration: mobile ? 0.4 : 0.5,
					stagger: mobile ? 0.08 : 0.1,
					ease: "power2.out",
				},
				mobile ? 0.28 : 0.32,
			)
		}
	})

	revealWhenInView(partners, () => {
		gsap.to(partners, {
			opacity: 1,
			y: 0,
			duration: mobile ? 0.55 : 0.7,
			ease: "power2.out",
		})
	})
}

function initContactAnim(root) {
	const section = find(".hm-contact", root)
	if (!section) return

	const mobile = isMobile()
	const bgs = findAll(".hm-contact__bg", section)
	const wrap = find(".hm-contact__form-wrap", section)
	const title = find(".hm-contact__header .hm-display", section)
	const intro = find(".hm-contact__header p", section)
	const fields = findAll(".hm-contact__form .hm-field", section)
	const submit = find(".hm-contact__form .btn-home-submit", section)

	const bits = [...bgs, wrap, title, intro, ...fields, submit].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(bits, { opacity: 1, clearProps: "transform" })
		return
	}

	gsap.set(bgs, { opacity: 0, scale: mobile ? 1.04 : 1.08 })
	if (wrap) {
		gsap.set(
			wrap,
			mobile
				? { opacity: 0, y: 40, scale: 0.96 }
				: { opacity: 0, y: 64, scale: 0.94, rotate: -1.5 },
		)
	}
	gsap.set([title, intro].filter(Boolean), { opacity: 0, y: 14 })
	gsap.set(fields, mobile ? { opacity: 0, y: 16 } : { opacity: 0, x: -28 })
	if (submit) gsap.set(submit, { opacity: 0, y: 12, scale: 0.92 })

	revealOnScroll(section, () => {
		gsap.to(bgs, {
			opacity: 1,
			scale: 1,
			duration: mobile ? 0.7 : 1,
			ease: "power2.out",
		})
	})

	revealOnScroll(wrap, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })

		tl.to(
			wrap,
			{
				opacity: 1,
				y: 0,
				scale: 1,
				rotate: 0,
				duration: mobile ? 0.45 : 0.6,
				ease: "power3.out",
			},
			0,
		)

		if (title) tl.to(title, { opacity: 1, y: 0, duration: 0.4 }, 0.16)
		if (intro) tl.to(intro, { opacity: 1, y: 0, duration: 0.35 }, 0.26)

		fields.forEach((field, i) => {
			tl.to(
				field,
				mobile
					? { opacity: 1, y: 0, duration: 0.3 }
					: { opacity: 1, x: 0, duration: 0.35 },
				0.3 + i * (mobile ? 0.05 : 0.07),
			)
		})

		if (submit) {
			tl.to(
				submit,
				{ opacity: 1, y: 0, scale: 1, duration: 0.35, ease: "back.out(1.5)" },
				0.3 + fields.length * (mobile ? 0.05 : 0.07),
			)
		}

		// remove transform residual — evita select/popup deslocado no viewport
		tl.eventCallback("onComplete", () => {
			gsap.set([wrap, title, intro, ...fields, submit].filter(Boolean), {
				clearProps: "transform",
			})
		})
	})
}

function initContactSelect(root) {
	const wraps = findAll("[data-hm-select]", root)
	if (!wraps?.length) return

	wraps.forEach((wrap) => {
		const native = find("select.hm-select__native", wrap)
		const trigger = find(".hm-select__trigger", wrap)
		const valueEl = find(".hm-select__value", wrap)
		const list = find(".hm-select__list", wrap)
		if (!native || !trigger || !valueEl || !list) return

		const placeholder =
			native.querySelector("option[value='']")?.textContent?.trim() || "Selecione..."

		const options = [...native.options].filter((opt) => !opt.disabled && opt.value !== "")

		list.innerHTML = ""
		options.forEach((opt) => {
			const li = document.createElement("li")
			li.setAttribute("role", "presentation")

			const btn = document.createElement("button")
			btn.type = "button"
			btn.className = "hm-select__option"
			btn.setAttribute("role", "option")
			btn.setAttribute("data-value", opt.value)
			btn.textContent = opt.textContent
			btn.setAttribute("aria-selected", "false")

			li.appendChild(btn)
			list.appendChild(li)
		})

		const optionBtns = () => [...list.querySelectorAll(".hm-select__option")]

		function syncFromNative() {
			const selected = native.options[native.selectedIndex]
			const hasValue = selected && selected.value

			valueEl.textContent = hasValue ? selected.textContent : placeholder
			valueEl.classList.toggle("hm-select__value--placeholder", !hasValue)

			optionBtns().forEach((btn) => {
				const isSelected = btn.dataset.value === native.value
				btn.classList.toggle("is-selected", isSelected)
				btn.setAttribute("aria-selected", String(isSelected))
			})
		}

		function positionList() {
			const rect = trigger.getBoundingClientRect()
			const gap = 8
			const vw = window.innerWidth
			const vh = window.innerHeight

			list.hidden = false
			list.style.visibility = "hidden"
			list.style.left = "0"
			list.style.top = "0"
			list.style.width = `${rect.width}px`
			list.style.maxWidth = `${Math.min(rect.width, vw - 16)}px`

			const listH = list.offsetHeight
			const spaceBelow = vh - rect.bottom - gap
			const spaceAbove = rect.top - gap
			const openUp = spaceBelow < listH && spaceAbove > spaceBelow

			let left = rect.left
			const width = Math.min(rect.width, vw - 16)
			if (left + width > vw - 8) left = vw - 8 - width
			if (left < 8) left = 8

			list.style.left = `${left}px`
			list.style.width = `${width}px`

			if (openUp) {
				const top = Math.max(8, rect.top - gap - listH)
				list.style.top = `${top}px`
				list.style.maxHeight = `${Math.min(280, rect.top - gap - 8)}px`
			} else {
				list.style.top = `${rect.bottom + gap}px`
				list.style.maxHeight = `${Math.min(280, vh - rect.bottom - gap - 8)}px`
			}

			list.style.visibility = ""
		}

		function close() {
			if (!wrap.classList.contains("is-open")) return
			wrap.classList.remove("is-open")
			trigger.setAttribute("aria-expanded", "false")
			list.hidden = true
			// devolve ao wrap pra manter o DOM coerente quando fechado
			if (list.parentElement !== wrap) wrap.appendChild(list)
			window.removeEventListener("resize", onReposition)
			window.removeEventListener("scroll", onReposition, true)
		}

		function open() {
			wrap.classList.add("is-open")
			trigger.setAttribute("aria-expanded", "true")
			// body evita clip/transform da seção .hm-contact
			document.body.appendChild(list)
			positionList()
			window.addEventListener("resize", onReposition)
			window.addEventListener("scroll", onReposition, true)

			const selected = optionBtns().find((btn) => btn.classList.contains("is-selected"))
			;(selected || optionBtns()[0])?.focus()
		}

		function onReposition() {
			if (wrap.classList.contains("is-open")) positionList()
		}

		function toggle() {
			if (wrap.classList.contains("is-open")) close()
			else open()
		}

		trigger.addEventListener("click", (e) => {
			e.preventDefault()
			toggle()
		})

		list.addEventListener("click", (e) => {
			const btn = e.target.closest(".hm-select__option")
			if (!btn) return
			native.value = btn.dataset.value
			native.dispatchEvent(new Event("change", { bubbles: true }))
			syncFromNative()
			close()
			trigger.focus()
		})

		trigger.addEventListener("keydown", (e) => {
			if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
				e.preventDefault()
				if (!wrap.classList.contains("is-open")) open()
			} else if (e.key === "Escape") {
				close()
			}
		})

		list.addEventListener("keydown", (e) => {
			const items = optionBtns()
			const idx = items.indexOf(document.activeElement)

			if (e.key === "Escape") {
				e.preventDefault()
				close()
				trigger.focus()
				return
			}

			if (e.key === "ArrowDown") {
				e.preventDefault()
				items[Math.min(idx + 1, items.length - 1)]?.focus()
			} else if (e.key === "ArrowUp") {
				e.preventDefault()
				items[Math.max(idx - 1, 0)]?.focus()
			} else if (e.key === "Home") {
				e.preventDefault()
				items[0]?.focus()
			} else if (e.key === "End") {
				e.preventDefault()
				items[items.length - 1]?.focus()
			} else if (e.key === "Enter" || e.key === " ") {
				e.preventDefault()
				document.activeElement?.click()
			}
		})

		document.addEventListener("click", (e) => {
			if (!wrap.contains(e.target) && !list.contains(e.target)) close()
		})

		native.addEventListener("invalid", () => {
			trigger.focus()
			wrap.classList.add("is-invalid")
		})

		native.addEventListener("change", () => {
			wrap.classList.remove("is-invalid")
		})

		const form = native.closest("form")
		form?.addEventListener("reset", () => {
			requestAnimationFrame(() => {
				syncFromNative()
				close()
				wrap.classList.remove("is-invalid")
			})
		})

		syncFromNative()
	})
}

function initVisitAnim(root) {
	const section = find(".hm-visit", root)
	if (!section) return

	const mobile = isMobile()
	const header = find(".hm-section-header", section)
	const title = find(".hm-section-header .hm-display", section)
	const intro = find(".hm-section-header p", section)
	const mapCard = find(".hm-visit__map-card", section)
	const map = find(".hm-visit__map", section)
	const address = find(".hm-visit__address", section)
	const infoCard = find(".hm-visit__info-card", section)
	const hours = find(".hm-visit__hours", section)
	const hourCols = findAll(".hm-hours-col", section)
	const important = find(".hm-visit__important", section)
	const faqCard = find(".hm-visit__faq-card", section)
	const faqIntro = find(".hm-visit__faq-intro", section)
	const faqItems = findAll(".hm-faq__item", section)

	const bits = [
		title,
		intro,
		map,
		address,
		hours,
		...hourCols,
		important,
		faqIntro,
		...faqItems,
	].filter(Boolean)

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
	if (reduceMotion) {
		gsap.set(bits, { opacity: 1, clearProps: "transform" })
		return
	}

	gsap.set([title, intro].filter(Boolean), { opacity: 0, y: mobile ? 16 : 24 })
	if (map) gsap.set(map, { opacity: 0, scale: 0.97, y: mobile ? 20 : 28 })
	if (address) {
		gsap.set(address, mobile ? { opacity: 0, y: 24 } : { opacity: 0, x: 40 })
	}
	if (hours) gsap.set(hours, { opacity: 0, y: 16 })
	gsap.set(hourCols, { opacity: 0, y: 18, scale: 0.92 })
	if (important) gsap.set(important, { opacity: 0, y: 18 })
	if (faqIntro) gsap.set(faqIntro, { opacity: 0, y: 18 })
	gsap.set(faqItems, { opacity: 0, y: 14 })

	revealOnScroll(header, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (title) tl.to(title, { opacity: 1, y: 0, duration: mobile ? 0.55 : 0.7 }, 0)
		if (intro) tl.to(intro, { opacity: 1, y: 0, duration: mobile ? 0.5 : 0.65 }, 0.16)
	})

	revealOnScroll(mapCard, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (map) {
			tl.to(map, { opacity: 1, scale: 1, y: 0, duration: mobile ? 0.45 : 0.6, ease: "power3.out" }, 0)
		}
		if (address) {
			tl.to(
				address,
				mobile
					? { opacity: 1, y: 0, duration: 0.4, ease: "power3.out" }
					: { opacity: 1, x: 0, duration: 0.55, ease: "power3.out" },
				mobile ? 0.1 : 0.13,
			)
		}
	})

	revealOnScroll(infoCard, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (hours) tl.to(hours, { opacity: 1, y: 0, duration: 0.4 }, 0)
		hourCols.forEach((col, i) => {
			tl.to(
				col,
				{ opacity: 1, y: 0, scale: 1, duration: 0.3, ease: "back.out(1.4)" },
				0.1 + i * (mobile ? 0.05 : 0.07),
			)
		})
		if (important) {
			tl.to(important, { opacity: 1, y: 0, duration: mobile ? 0.4 : 0.55 }, 0.2)
		}
	})

	revealOnScroll(faqCard, () => {
		const tl = gsap.timeline({ defaults: { ease: "power2.out" } })
		if (faqIntro) tl.to(faqIntro, { opacity: 1, y: 0, duration: mobile ? 0.4 : 0.55 }, 0)
		faqItems.forEach((item, i) => {
			tl.to(
				item,
				{ opacity: 1, y: 0, duration: mobile ? 0.3 : 0.35 },
				0.13 + i * (mobile ? 0.04 : 0.05),
			)
		})
	})
}

export default function () {
	const Home = find("[js-page='home']")
	if (!Home) return

	initHero(Home)
	initCountdown(Home)
	initAbout(Home)
	initDiff(Home)
	initTicketsAnim(Home)
	initAttractionsAnim(Home)
	initHighlightsAnim(Home)
	initExhibitorAnim(Home)
	initContactAnim(Home)
	initContactSelect(Home)
	initVisitAnim(Home)
	initHeadliner(Home)
	initAttractions(Home)
	initTickets(Home)
	initGallery(Home)
	initHighlights(Home)
	initPartners(Home)
	initFaq(Home)
}
